import { useCallback, useState } from "react";
import { api, getAccessToken, useAuth } from "../auth/AuthContext";
import { dismissOrgNamePrompt, shouldAskOrgName } from "../lib/orgNamePrompt";

/**
 * KEY-743: whether the "name your organisation" prompt is showing, and how to put it away. A hook rather than
 * state inside the card because the dial page needs the answer too: while this is asked, the other one-off
 * prompts there (follow, add people) wait, so a new buyer gets one question at a time and this one first.
 */
export function useOrgNamePrompt(): { show: boolean; dismiss: () => void } {
  const { me } = useAuth();
  // Bumped on "Not now" so the storage read below runs again; the storage itself is the record.
  const [, setDismissals] = useState(0);
  const show = shouldAskOrgName(me);
  const organisationId = me?.organisationId ?? null;
  const dismiss = useCallback(() => {
    if (organisationId) dismissOrgNamePrompt(organisationId);
    setDismissals((n) => n + 1);
  }, [organisationId]);
  return { show, dismiss };
}

/** The server's reason for a refusal, without the quotes a JSON-encoded string arrives in. */
export function readableRenameError(e: unknown): string {
  const raw = e instanceof Error ? e.message : "";
  const text = raw.replace(/^"(.*)"$/s, "$1").trim();
  return text || "Couldn't save the name. Please try again.";
}

/**
 * KEY-743: "Is ‘Acme’ the right name for your organisation?" — asked of a self-serve buyer whose organisation we
 * named from their email address. Save renames it (and the server then stops asking); "Not now" hides it in this
 * browser. Never compulsory, and Save is never greyed out: a blank name is refused with a line saying why
 * (KEY-741's rule — a disabled button doesn't tell anyone what's missing).
 */
export function OrgNamePrompt({ organisationName, onDismiss }: { organisationName: string; onDismiss: () => void }) {
  const { refreshMe } = useAuth();
  const [name, setName] = useState(organisationName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (busy) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Give your organisation a name before saving.");
      return;
    }
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.renameMyOrganisation(token, trimmed);
      // The new name is read back from /api/me, which also stops saying it is guessed — that is what hides this.
      await refreshMe();
    } catch (e) {
      setError(readableRenameError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="kd-card"
      role="region"
      aria-label="Name your organisation"
      style={{ padding: "var(--pad-card, 16px)", margin: "0 0 14px", borderLeft: "3px solid var(--accent)" }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text)" }}>
        Is ‘{organisationName}’ the right name for your organisation?
      </div>
      <p style={{ margin: "6px 0 12px", fontSize: 13.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
        We guessed it from your email address.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}
      >
        <input
          aria-label="Organisation name"
          value={name}
          maxLength={200}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError(null);
          }}
          style={{
            font: "inherit",
            fontSize: 14,
            padding: "8px 11px",
            borderRadius: "var(--radius-sm)",
            border: "var(--border-w) solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            flex: "1 1 220px",
            maxWidth: 380,
          }}
        />
        <button type="submit" className="kd-btn-primary" style={{ fontSize: 13 }} aria-busy={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
        {/* kd-btn-dashed is the full-width "add" button; sized to its label here, as a secondary answer. */}
        <button
          type="button"
          onClick={onDismiss}
          className="kd-btn-dashed"
          style={{ fontSize: 13, width: "auto", padding: "8px 16px" }}
        >
          Not now
        </button>
      </form>
      {error && (
        <div role="alert" style={{ marginTop: 8, fontSize: 13, color: "var(--st-overdue)" }}>
          {error}
        </div>
      )}
      <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "var(--text-faint)" }}>
        You can change this any time under Your settings.
      </p>
    </div>
  );
}
